import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useForm } from 'react-hook-form';
import { featureEnabled } from '@grafana/runtime';
import { Checkbox, Field, FieldSet, LinkButton, RadioButtonGroup, InputControl } from '@grafana/ui';
import { AppEvents, urlUtil } from '@grafana/data';
import { GrafanaRouteComponentProps } from 'app/core/navigation/types';
import { appEvents } from 'app/core/core';
import { getVariablesByKey } from 'app/features/variables/state/selectors';
import {
  EnterpriseStoreState,
  ReportFormData,
  ReportLayout,
  reportLayouts,
  reportOrientations,
  StepKey,
} from '../../types';
import { getRange } from '../../utils/time';
import { getRendererMajorVersion } from '../utils/renderer';
import { variablesToCsv } from '../utils/variables';
import { updateReportProp } from '../state/reducers';
import { getLastUid } from '../state/selectors';
import ReportForm from './ReportForm';
import { canEditReport } from '../utils/permissions';

interface OwnProps extends GrafanaRouteComponentProps<{ id: string }> {}

const mapStateToProps = (state: EnterpriseStoreState) => {
  const lastUid = getLastUid(state);
  return {
    report: state.reports.report,
    variables: lastUid ? getVariablesByKey(lastUid) : [],
  };
};

const mapActionsToProps = {
  updateReportProp,
};

const connector = connect(mapStateToProps, mapActionsToProps);
export type Props = ConnectedProps<typeof connector> & OwnProps;

const descriptions = new Map<ReportLayout, string>([
  ['grid', 'Display the panels in their positions on the dashboard.'],
  ['simple', 'Display one panel per row.'],
]);

export const StyleReport = ({ report, variables, updateReportProp }: Props) => {
  const {
    handleSubmit,
    control,
    register,
    watch,
    formState: { isDirty },
  } = useForm();
  const { dashboardId, enableCsv, options, name } = report || {};
  const orientation = options.orientation || 'landscape';
  const layout = options.layout || 'grid';
  const watchLayout = watch('layout', layout);
  const rendererMajorVersion = getRendererMajorVersion();

  const saveData = ({ enableCsv, options }: Partial<ReportFormData>) => {
    if (isDirty) {
      updateReportProp({ ...report, options: { ...report.options, ...options }, enableCsv });
    }
  };

  const getPreviewUrl = (dashboardId: ReportFormData['dashboardId']) => {
    if (!dashboardId) {
      return undefined;
    }

    const { from, to } = getRange(options.timeRange).raw;

    const params: any = {
      title: name,
      from: from.valueOf(),
      to: to.valueOf(),
    };

    if (options.orientation) {
      params.orientation = options.orientation;
    }

    if (options.layout) {
      params.layout = options.layout;
    }

    if (variables?.length) {
      params.variables = JSON.stringify(variablesToCsv(variables));
    }

    return urlUtil.appendQueryToUrl(`api/reports/render/pdf/${dashboardId}`, urlUtil.toUrlParams(params));
  };

  return (
    <ReportForm activeStep={StepKey.StyleReport} onSubmit={handleSubmit(saveData)} confirmRedirect={isDirty}>
      <FieldSet label={'2. Style report'} disabled={!canEditReport}>
        <Field label="Orientation">
          <InputControl
            name={'options.orientation'}
            control={control}
            defaultValue={orientation}
            render={({ field: { ref, ...field } }) => {
              return <RadioButtonGroup {...field} options={reportOrientations} size={'md'} />;
            }}
          />
        </Field>
        <Field label="Layout" description={descriptions.get(watchLayout)}>
          <InputControl
            name={'options.layout'}
            control={control}
            defaultValue={layout}
            render={({ field: { ref, ...field } }) => {
              return <RadioButtonGroup {...field} options={reportLayouts} size={'md'} />;
            }}
          />
        </Field>

        <Field disabled={!canEditReport}>
          <Checkbox
            {...register('enableCsv')}
            defaultChecked={enableCsv}
            label="Add a CSV file of table panel data"
            onChange={(event: React.FormEvent<HTMLInputElement>) => {
              const enabled = event.currentTarget.checked;
              if (enabled && rendererMajorVersion !== null && rendererMajorVersion < 3) {
                appEvents.emit(AppEvents.alertError, [
                  'To export CSV files, you must update the Grafana Image Renderer plugin.',
                ]);
              }
            }}
          />
        </Field>
        <Field disabled={!featureEnabled('reports.email') || !dashboardId}>
          <LinkButton
            icon={'external-link-alt'}
            href={getPreviewUrl(dashboardId)}
            size="xs"
            target="_blank"
            rel="noreferrer noopener"
            variant="secondary"
          >
            Preview PDF
          </LinkButton>
        </Field>
      </FieldSet>
    </ReportForm>
  );
};

export default connector(StyleReport);
